import { useEffect, useState } from "react";
import { Pencil, Plus, Search, Trash2, Warehouse, X } from "lucide-react";
import { api } from "@/lib/api";
import { useEscape } from "@/lib/useEscape";

export default function Warehouses() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState({});
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState(null);
  const load = async () => {
    try {
      const whs = await api("/warehouses");
      setRows(whs);
      const maps = await Promise.all(whs.map((w) => api(`/inventory/warehouse-stock/${w.id}`).then((r) => [w.id, r.stock]).catch(() => [w.id, {}])));
      setStocks(Object.fromEntries(maps));
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
  };
  useEffect(() => { load(); api("/products").then(setProducts).catch(() => {}); }, []);
  const pmap = Object.fromEntries(products.map((p) => [p.id, p]));
  const stat = (id) => {
    const m = stocks[id] || {};
    const items = Object.entries(m).filter(([, q]) => q > 0);
    return { kinds: items.length, qty: items.reduce((s, [, q]) => s + q, 0) };
  };
  const filtered = rows.filter((w) => [w.name, w.location].join(" ").toLowerCase().includes(search.toLowerCase()));
  const remove = async () => {
    try {
      await api(`/warehouses/${deleting.id}`, { method: "DELETE" });
      setMessage({ text: `Gudang "${deleting.name}" berhasil dihapus`, type: "success" });
      load();
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
    setDeleting(null);
  };
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div><p className="eyebrow">PRODUK & GUDANG</p><h1>Gudang</h1><p className="muted">Kelola lokasi penyimpanan dan pantau stok di setiap gudang.</p></div>
        <button data-testid="wh-create-button" className="primary-btn" onClick={() => setEditing({})}><Plus size={16} /> Tambah Gudang</button>
      </div>
      {message && <div data-testid={message.type === "error" ? "wh-error-message" : "wh-success-message"} className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button onClick={() => setMessage(null)}><X size={14} /></button></div>}
      <div className="workflow-toolbar surface">
        <div className="workflow-search"><Search size={16} /><input data-testid="wh-search-input" placeholder="Cari gudang..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <span className="record-count">{filtered.length} gudang</span>
      </div>
      <div className="surface workflow-table-wrap auto-h">
        <table className="workflow-table" data-testid="wh-table">
          <thead><tr><th>NAMA GUDANG</th><th>LOKASI</th><th className="right">JENIS PRODUK</th><th className="right">TOTAL QTY</th><th>AKSI</th></tr></thead>
          <tbody>
            {filtered.map((w) => {
              const s = stat(w.id);
              return (
                <tr key={w.id} onClick={() => setSelected(w)} data-testid={`wh-row-${w.id}`}>
                  <td className="primary-cell">{w.name}{w.is_main && <span className="wh-main-badge">Utama</span>}</td>
                  <td>{w.location || "—"}</td>
                  <td className="right mono">{s.kinds}</td>
                  <td className="right mono">{s.qty}</td>
                  <td><span className="row-actions">
                    <button data-testid={`wh-edit-${w.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setEditing(w); }}>Edit</button>
                    {!w.is_main && <button data-testid={`wh-delete-${w.id}`} className="table-action danger" onClick={(e) => { e.stopPropagation(); setDeleting(w); }}>Hapus</button>}
                  </span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state" data-testid="wh-empty-state"><Warehouse size={25} /><strong>Belum ada gudang</strong><span>Tambah gudang penyimpanan pertama Anda.</span></div>}
      </div>
      {editing && <WarehouseModal initial={editing.id ? editing : null} onClose={() => setEditing(null)}
        onSaved={(r, isEdit) => { setEditing(null); load(); setMessage({ text: `Gudang "${r.name}" berhasil ${isEdit ? "diperbarui" : "ditambahkan"}`, type: "success" }); }} />}
      {deleting && (
        <div className="modal-overlay" data-testid="wh-delete-modal" onClick={() => setDeleting(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="form-panel">
              <div className="section-head"><div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus Gudang?</h2></div><button className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button></div>
              <p className="muted delete-warning">"{deleting.name}" akan dihapus. Gudang yang masih memiliki stok tidak dapat dihapus — transfer stok terlebih dahulu.</p>
              <div className="form-actions">
                <button data-testid="wh-delete-confirm" className="danger-btn" onClick={remove}>Ya, Hapus</button>
                <button data-testid="wh-delete-cancel" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {selected && <WarehouseDrawer warehouse={selected} stock={stocks[selected.id] || {}} pmap={pmap} onClose={() => setSelected(null)} onEdit={() => { setEditing(selected); setSelected(null); }} />}
    </div>
  );
}

function WarehouseModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ name: initial?.name || "", location: initial?.location || "" });
  const [error, setError] = useState("");
  const submit = async () => {
    try {
      const r = initial ? await api(`/warehouses/${initial.id}`, { method: "PATCH", body: JSON.stringify(form) }) : await api("/warehouses", { method: "POST", body: JSON.stringify(form) });
      onSaved(r, Boolean(initial));
    } catch (e) { setError(e.message); }
  };
  return (
    <div className="modal-overlay" data-testid="wh-form-modal">
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head"><div><p className="eyebrow">GUDANG</p><h2>{initial ? "Edit" : "Tambah"} Gudang</h2></div><button data-testid="wh-form-close" className="icon-btn" onClick={onClose}><X size={18} /></button></div>
          {error && <div className="journal-balance bad" data-testid="wh-form-error">{error}</div>}
          <div className="form-grid">
            <label>Nama gudang *<input data-testid="wh-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label>Lokasi<input data-testid="wh-field-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Opsional" /></label>
          </div>
          <div className="form-actions">
            <button data-testid="wh-save-button" className="primary-btn" disabled={!form.name.trim()} onClick={submit}>{initial ? "Simpan Perubahan" : "Simpan Gudang"}</button>
            <button data-testid="wh-cancel-button" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WarehouseDrawer({ warehouse, stock, pmap, onClose, onEdit }) {
  useEscape(onClose);
  const items = Object.entries(stock).filter(([pid, q]) => q !== 0 && pmap[pid]).map(([pid, q]) => ({ id: pid, name: pmap[pid].name, sku: pmap[pid].sku || "", unit: pmap[pid].unit || "", qty: q }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const totalQty = items.reduce((s, i) => s + i.qty, 0);
  return (
    <>
    <div className="drawer-backdrop" data-testid="wh-drawer-backdrop" onClick={onClose} />
    <div className="detail-drawer v2" data-testid="wh-detail-drawer">
      <div className="drawer-hero">
        <div className="drawer-hero-top">
          <div>
            <p className="eyebrow">DETAIL GUDANG</p>
            <h2>{warehouse.name}</h2>
            {warehouse.location && <p className="drawer-hero-party">{warehouse.location}</p>}
          </div>
          <button data-testid="wh-close-detail" className="icon-btn light" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="drawer-hero-stats two">
          <div><small>Jenis Produk</small><b>{items.length}</b></div>
          <div><small>Total Kuantitas</small><b>{totalQty}</b></div>
        </div>
      </div>
      <div className="drawer-body">
        <div className="doc-lines v2">
          <div className="drawer-section-title">Stok di Gudang Ini</div>
          {items.length === 0 ? <p className="muted">Belum ada stok di gudang ini.</p> : (
            <table className="doc-line-table v2" data-testid="wh-stock-table">
              <thead><tr><th>PRODUK</th><th>SKU</th><th className="right">QTY</th></tr></thead>
              <tbody>
                {items.map((i) => <tr key={i.id}><td>{i.name}</td><td className="mono">{i.sku || "—"}</td><td className="right mono">{i.qty} {i.unit}</td></tr>)}
              </tbody>
            </table>
          )}
        </div>
        <div className="drawer-section-title">Tindakan</div>
        <div className="drawer-actions v2">
          <button data-testid="wh-drawer-edit" className="secondary-btn" onClick={onEdit}><Pencil size={14} /> Edit Gudang</button>
        </div>
      </div>
    </div>
    </>
  );
}
